import {
  isNormalizedRaceScheduledEvent,
  isNormalizedRaceSettledEvent,
  isNormalizedRaceEndedEvent,
  isNormalizedRaceStartedEvent,
  isNormalizedRaceReadyEvent,
  isNormalizedRaceCrashedEvent
} from 'src/store/types'
import type {
  NormalizedRaceScheduledEvent,
  NormalizedRaceEvent,
  NormalizedRaceSettledEvent,
  NormalizedRaceEndedEvent,
  NormalizedRaceStartedEvent,
  NormalizedRaceReadyEvent,
  NormalizedRaceCrashedEvent
} from 'src/store/types'
import {
  isRaceScheduledEvent,
  isRaceSettledEvent,
  isRaceEndedEvent,
  isRaceStartedEvent,
  isRaceReadyEvent,
  isRaceCrashedEvent
} from '../../domain/RaceEvent'
import { roundOddsFromWire } from '../../domain/RoundOdds'
import { hardCloseFromWire } from '../../domain/HardClose'
import type {
  RaceScheduledEvent,
  RaceEvent,
  RaceSettledEvent,
  RaceEndedEvent,
  RaceStartedEvent,
  RaceReadyEvent
} from '../../domain/RaceEvent'
import type { Tables } from '../../infra/supabase/database.types'

export const fromRaceEventRowDTO = (row: Tables<'race_event'>): RaceEvent => {
  if (row.event_type === 'race_settled') {
    const raceSettledEvent: RaceSettledEvent = {
      uuid: row.id,
      raceId: row.race_id,
      eventType: 'race_settled',
      eventTimestamp: row.event_timestamp,
      payload: row.payload as RaceSettledEvent['payload']
    }

    return raceSettledEvent
  }

  if (row.event_type === 'race_scheduled') {
    const raceScheduledEvent: RaceScheduledEvent = {
      uuid: row.id,
      raceId: row.race_id,
      eventType: 'race_scheduled',
      eventTimestamp: row.event_timestamp,
      payload: row.payload as RaceScheduledEvent['payload']
    }

    return raceScheduledEvent
  }

  if (row.event_type === 'race_ended') {
    const raceEndedEvent: RaceEndedEvent = {
      uuid: row.id,
      raceId: row.race_id,
      eventType: 'race_ended',
      eventTimestamp: row.event_timestamp,
      payload: row.payload as RaceEndedEvent['payload']
    }

    return raceEndedEvent
  }

  if (row.event_type === 'race_ready') {
    // Crash games attach a provable-fairness commitment to race_ready:
    // `server_seed_hash` (snake_case on the wire) — a sha256 of the
    // round's secret server seed AND its nonce ("seed:nonce"), published
    // before betting opens.
    // Normalize to the camelCase domain field. Non-crash games (and
    // pre-deploy rounds) carry no payload — the optional field stays
    // undefined and the widget gates rendering on its presence.
    const payload = row.payload as { server_seed_hash?: string; odds?: unknown } | null
    const serverSeedHash = payload?.server_seed_hash
    // Dynamic-odds games put the round's board inside `odds` (snake_case on
    // the wire). A malformed object maps to undefined — the board then stays
    // unpriced and every option non-bettable, never a half-priced board.
    const odds = roundOddsFromWire(payload?.odds)
    // The hard close (`{window_s, lock_lead_s, bet_close, hold_s}` at the top
    // level of the payload): kept only on a round the engine sent hard, so
    // every other round maps exactly as before.
    const hardClose = hardCloseFromWire(payload)

    const raceReadyEvent: RaceReadyEvent = {
      uuid: row.id,
      raceId: row.race_id,
      eventType: 'race_ready',
      eventTimestamp: row.event_timestamp,
      ...(serverSeedHash || odds || hardClose
        ? {
            payload: {
              ...(serverSeedHash ? { serverSeedHash } : {}),
              ...(odds ? { odds } : {}),
              ...(hardClose ? { hardClose } : {})
            }
          }
        : {})
    }

    return raceReadyEvent
  }

  if (row.event_type === 'race_started') {
    // CCTV games attach a boundaries + optionLabels payload here on
    // the betting-closed transition. Crash games attach a different
    // shape (`{game_mode: "crash"}`) — both flow through this branch
    // since `payload` is `unknown`-ish on the row DTO and the
    // chip-placement panel only reads boundaries/optionLabels via
    // the optional-payload type guard. Other games (and pre-deploy
    // CCTV rounds) carry no payload at all — typed event still
    // produced; payload absence is the gating signal.
    const raceStartedEvent: RaceStartedEvent = {
      uuid: row.id,
      raceId: row.race_id,
      eventType: 'race_started',
      eventTimestamp: row.event_timestamp,
      payload: row.payload as RaceStartedEvent['payload']
    }

    return raceStartedEvent
  }

  return {
    uuid: row.id,
    raceId: row.race_id,
    eventType: row.event_type,
    eventTimestamp: row.event_timestamp
  }
}

export const fromNormalizedRaceEvent = (normalizedRaceEvents: NormalizedRaceEvent): RaceEvent => {
  if (isNormalizedRaceSettledEvent(normalizedRaceEvents)) {
    const raceSettledEvent: RaceSettledEvent = {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: normalizedRaceEvents.eventType,
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      payload: normalizedRaceEvents.payload
    }

    return raceSettledEvent
  }

  if (isNormalizedRaceScheduledEvent(normalizedRaceEvents)) {
    const raceScheduledEvent: RaceScheduledEvent = {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: normalizedRaceEvents.eventType,
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      payload: normalizedRaceEvents.payload
    }

    return raceScheduledEvent
  }

  // Handle race_ended events with crash multiplier payload
  if (isNormalizedRaceEndedEvent(normalizedRaceEvents)) {
    const raceEndedEvent: RaceEndedEvent = {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: 'race_ended',
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      payload: normalizedRaceEvents.payload
    }

    return raceEndedEvent
  }

  // race_started carries the CCTV boundaries payload (when present)
  // — preserve it through the normalization round-trip so the
  // chip-placement panel can read it via `find(isRaceStartedEvent)`.
  if (isNormalizedRaceStartedEvent(normalizedRaceEvents)) {
    const raceStartedEvent: RaceStartedEvent = {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: 'race_started',
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      payload: normalizedRaceEvents.payload
    }
    return raceStartedEvent
  }

  // race_ready carries the crash provable-fairness commitment
  // (`serverSeedHash`) — preserve it so the crash panel can read it
  // via `find(isRaceReadyEvent)`.
  if (isNormalizedRaceReadyEvent(normalizedRaceEvents)) {
    const raceReadyEvent: RaceReadyEvent = {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: 'race_ready',
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      payload: normalizedRaceEvents.payload
    }
    return raceReadyEvent
  }

  // race_crashed (synthetic, from the crash:occurred broadcast) carries
  // the crash multiplier — the only M available at the visual-crash
  // instant. Preserve it so the hero stamp / crash surfaces can read it;
  // stripping it caused the "1.00x → 2.12x" stamp flash.
  if (isNormalizedRaceCrashedEvent(normalizedRaceEvents)) {
    return {
      uuid: normalizedRaceEvents.uuid,
      raceId: normalizedRaceEvents.raceId,
      eventType: 'race_crashed',
      eventTimestamp: normalizedRaceEvents.eventTimestamp,
      ...(normalizedRaceEvents.payload ? { payload: normalizedRaceEvents.payload } : {})
    } as RaceEvent
  }

  return {
    uuid: normalizedRaceEvents.uuid,
    raceId: normalizedRaceEvents.raceId,
    eventType: normalizedRaceEvents.eventType,
    eventTimestamp: normalizedRaceEvents.eventTimestamp
  }
}

export const toNormalizedRaceEvent = (raceEvent: RaceEvent): NormalizedRaceEvent => {
  if (isRaceSettledEvent(raceEvent)) {
    const normalizedRaceSettledEvent: NormalizedRaceSettledEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: raceEvent.eventType,
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload
    }

    return normalizedRaceSettledEvent
  }

  if (isRaceScheduledEvent(raceEvent)) {
    const normalizedRaceScheduledEvent: NormalizedRaceScheduledEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: raceEvent.eventType,
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload ?? { start_timestamp: '' }
    }

    return normalizedRaceScheduledEvent
  }

  // Handle race_ended events with crash multiplier payload
  if (isRaceEndedEvent(raceEvent)) {
    const normalizedRaceEndedEvent: NormalizedRaceEndedEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: 'race_ended',
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload
    }
    return normalizedRaceEndedEvent
  }

  // race_started carries the CCTV boundaries payload — preserve it
  // through normalization so the chip-placement store keeps the
  // data accessible via `currentRace.raceEvents`. Without this
  // branch the default fallthrough below silently drops `payload`.
  if (isRaceStartedEvent(raceEvent)) {
    const normalizedRaceStartedEvent: NormalizedRaceStartedEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: 'race_started',
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload
    }
    return normalizedRaceStartedEvent
  }

  // race_ready carries the crash provable-fairness commitment
  // (`serverSeedHash`) — preserve it through normalization so the
  // crash panel can read it from `currentRace.raceEvents`. Without
  // this branch the default fallthrough silently drops `payload`.
  if (isRaceReadyEvent(raceEvent)) {
    const normalizedRaceReadyEvent: NormalizedRaceReadyEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: 'race_ready',
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload
    }
    return normalizedRaceReadyEvent
  }

  // race_crashed carries the authoritative crash multiplier from the
  // crash:occurred broadcast — preserve it through normalization so the
  // hero stamp knows M at the visual-crash instant. Without this branch
  // the default fallthrough silently drops `payload` (the "1.00x flash").
  if (isRaceCrashedEvent(raceEvent)) {
    const normalizedRaceCrashedEvent: NormalizedRaceCrashedEvent = {
      uuid: raceEvent.uuid,
      raceId: raceEvent.raceId,
      eventType: 'race_crashed',
      eventTimestamp: raceEvent.eventTimestamp,
      payload: raceEvent.payload
    }
    return normalizedRaceCrashedEvent
  }

  return {
    uuid: raceEvent.uuid,
    raceId: raceEvent.raceId,
    eventType: raceEvent.eventType,
    eventTimestamp: raceEvent.eventTimestamp
  }
}
